import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { CopyResultButton } from "@/components/CopyResultButton";

export default function EquilibriumConstantCalculator() {
  const [products, setProducts] = useState("");
  const [reactants, setReactants] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculate = () => {
    const prod = parseFloat(products);
    const react = parseFloat(reactants);

    if (isNaN(prod) || isNaN(react) || react === 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid numbers. Reactants cannot be zero.",
        variant: "destructive",
      });
      return;
    }

    const kc = prod / react;
    setResult(kc);

    toast({
      title: "Calculation Complete",
      description: `Kc = ${kc.toExponential(2)}`,
    });
  };

  const reset = () => {
    setProducts("");
    setReactants("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Equilibrium Constant Calculator" 
      calculatorPath="/calculator/equilibrium-constant"
      category="chemistry"
      subcategory="Equilibrium"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Equilibrium Constant Calculator</CardTitle>
            <CardDescription>
              Calculate Kc/Kp for chemical equilibrium
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="products">[Products] (concentration product)</Label>
                <Input
                  id="products"
                  type="number"
                  step="any"
                  value={products}
                  onChange={(e) => setProducts(e.target.value)}
                  placeholder="Product of product concentrations"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reactants">[Reactants] (concentration product)</Label>
                <Input
                  id="reactants"
                  type="number"
                  step="any"
                  value={reactants}
                  onChange={(e) => setReactants(e.target.value)}
                  placeholder="Product of reactant concentrations"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result !== null && (
              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">Equilibrium Constant (Kc)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start justify-between gap-4">
                    <p className="text-2xl font-bold break-all overflow-wrap-anywhere flex-1">{result.toExponential(2)}</p>
                    <CopyResultButton value={result.toExponential(2)} />
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Formula</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-mono">Kc = [Products] / [Reactants]</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Each concentration should be raised to its stoichiometric coefficient
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
